const STATE_TIMEZONE = {
    AL: 'America/Chicago',
    AK: 'America/Anchorage',
    AZ: 'America/Phoenix',
    AR: 'America/Chicago',
    CA: 'America/Los_Angeles',
    CO: 'America/Denver',
    CT: 'America/New_York',
    DC: 'America/New_York',
    DE: 'America/New_York',
    FL: 'America/New_York',
    GA: 'America/New_York',
    GU: 'Pacific/Guam',
    HI: 'Pacific/Honolulu',
    ID: 'America/Boise',
    IL: 'America/Chicago',
    IN: 'America/Indiana/Indianapolis',
    IA: 'America/Chicago',
    KS: 'America/Chicago',
    KY: 'America/New_York',
    LA: 'America/Chicago',
    ME: 'America/New_York',
    MD: 'America/New_York',
    MA: 'America/New_York',
    MI: 'America/Detroit',
    MN: 'America/Chicago',
    MS: 'America/Chicago',
    MO: 'America/Chicago',
    MT: 'America/Denver',
    NE: 'America/Chicago',
    NV: 'America/Los_Angeles',
    NH: 'America/New_York',
    NJ: 'America/New_York',
    NM: 'America/Denver',
    NY: 'America/New_York',
    NC: 'America/New_York',
    ND: 'America/Chicago',
    OH: 'America/New_York',
    OK: 'America/Chicago',
    OR: 'America/Los_Angeles',
    PA: 'America/New_York',
    PR: 'America/Puerto_Rico',
    RI: 'America/New_York',
    SC: 'America/New_York',
    SD: 'America/Chicago',
    TN: 'America/Chicago',
    TX: 'America/Chicago',
    UT: 'America/Denver',
    VT: 'America/New_York',
    VI: 'America/St_Thomas',
    VA: 'America/New_York',
    WA: 'America/Los_Angeles',
    WV: 'America/New_York',
    WI: 'America/Chicago',
    WY: 'America/Denver',
};
/**
 * Maps a US state code to an IANA timezone string.
 * Multi-timezone states resolve to the most populous timezone.
 * Falls back to 'America/New_York' for unknown inputs.
 */
export function deriveTimezone(state) {
    return STATE_TIMEZONE[state.toUpperCase()] ?? 'America/New_York';
}
const DEFAULT_CALENDAR_OPTIONS = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
};
const DEFAULT_TIMESTAMP_OPTIONS = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
};
/**
 * Formats a YYYY-MM-DD calendar date string for display.
 *
 * A calendar date (`@db.Date` — DOB, lease dates, due dates, scheduled-run dates, ...) has no
 * time-of-day and therefore no timezone: "May 15" is May 15 everywhere. It takes no timezone
 * parameter on purpose — there is no correct timezone to pass, only degrees of coincidentally
 * correct ones. Rendering it by round-tripping through a `Date` object anchored to any single
 * zone (browser-local or otherwise) and reformatting in another is exactly the AP-002 bug this
 * function exists to prevent: it re-derives the Y/M/D digits from an instant, so a mismatch
 * between the construction zone and the display zone can shift the displayed day by one.
 * Instead, this reads the Y/M/D digits straight off the string and never manufactures an instant
 * that could be interpreted in more than one zone.
 *
 * Takes a `CalendarDateString` (from `@resida/api-types/shared`'s `calendarDateString`
 * schema), not a plain `string` — this is what makes a call like
 * `formatCalendarDate(entry.createdAt)` a compile error instead of a silently wrong day.
 * A field that hasn't migrated to that schema yet needs `asCalendarDateString()` below at
 * the call site (a visible, greppable marker, not a silent behavior change).
 *
 * Returns '—' for null/undefined.
 */
export function formatCalendarDate(dateStr, options = DEFAULT_CALENDAR_OPTIONS) {
    if (!dateStr)
        return '—';
    const parts = dateStr.split('-').map(Number);
    const date = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone: 'UTC' }).format(date);
}
/**
 * Formats an ISO UTC timestamp for display in a given IANA timezone.
 *
 * Takes an `IsoDateTimeString` (from `@resida/api-types/shared`'s `timestampString`
 * schema), not a plain `string` — the `CalendarDateString`/`IsoDateTimeString` pair is what
 * makes `formatTimestamp(entry.dueDate, tz)` a compile error rather than a day-shifted
 * display (AP-002, docs/claude/anti-patterns.md). A field that hasn't migrated to that
 * schema yet needs `asIsoDateTimeString()` below at the call site.
 *
 * Returns '—' for null/undefined.
 */
export function formatTimestamp(isoStr, timezone, options = DEFAULT_TIMESTAMP_OPTIONS) {
    if (!isoStr)
        return '—';
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone: timezone }).format(new Date(isoStr));
}
/**
 * Escape hatch for a `formatCalendarDate` call site whose underlying schema field hasn't
 * been migrated to `calendarDateString` yet (see `packages/api-types/src/shared/date.ts`).
 * Zero runtime effect — a type-only assertion, not a parse or a cast of the value itself.
 *
 * This is tracked migration debt, not a pattern to reach for in new code: fix the schema
 * field instead where you can. `grep -rn 'asCalendarDateString(' apps/ packages/` finds
 * every remaining call site still using this escape hatch.
 */
export function asCalendarDateString(value) {
    return value;
}
/** Same escape hatch as `asCalendarDateString`, for `formatTimestamp`/`IsoDateTimeString`. */
export function asIsoDateTimeString(value) {
    return value;
}
/**
 * Returns the current date as a YYYY-MM-DD string in the given IANA timezone.
 * Use this wherever "today" must be relative to the property, not the browser.
 */
export function todayInTimezone(timezone) {
    return new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(new Date());
}
const MS_PER_DAY = 86_400_000;
/** Whole days since the epoch for a calendar date given as its UTC y/m/d (no local-zone math). */
function utcDayNumber(year, month, day) {
    return Date.UTC(year, month - 1, day) / MS_PER_DAY;
}
/**
 * Whole calendar days from `today` (a YYYY-MM-DD string, e.g. from `todayInTimezone`) to a
 * date-only business field that the API serialises as an ISO string at UTC midnight (`dueAt`,
 * `overdueSince`, ...) or as a bare YYYY-MM-DD. Negative when `target` is in the past.
 *
 * A date-only field has no time of day, so "days late" is a calendar-day difference, not an
 * elapsed-time one. `(Date.now() - new Date(target).getTime()) / 86_400_000` mixes an instant with
 * UTC midnight and is off by one for most of the day in any non-UTC zone (AP-002); this reads the
 * target's UTC y/m/d and `today`'s y/m/d and subtracts as UTC calendar days. Do not use it for
 * real instants (createdAt, paidThrough) - those want elapsed time.
 */
export function calendarDaysUntil(target, today) {
    const t = new Date(target);
    const [y, m, d] = today.split('-').map(Number);
    return utcDayNumber(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate()) - utcDayNumber(y, m, d);
}
const OFFSET_FORMATTERS = new Map();
function getOffsetFormatter(timeZone) {
    let formatter = OFFSET_FORMATTERS.get(timeZone);
    if (!formatter) {
        // Throws RangeError for an unknown timeZone. hourCycle 'h23' matters: hour12:false can yield
        // "24" at midnight.
        formatter = new Intl.DateTimeFormat('en-US', {
            timeZone,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
        OFFSET_FORMATTERS.set(timeZone, formatter);
    }
    return formatter;
}
/** UTC offset (minutes, local minus UTC) of `timeZone` at the given instant. */
function offsetMinutesAt(epochMs, timeZone) {
    const parts = getOffsetFormatter(timeZone).formatToParts(new Date(epochMs));
    const get = (type) => Number(parts.find((p) => p.type === type)?.value);
    const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
    return Math.round((asUtc - Math.floor(epochMs / 1000) * 1000) / 60_000);
}
const LOCAL_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const LOCAL_TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const DAY_MS = 86_400_000;
/**
 * Interprets a wall-clock date+time in an IANA timezone and returns the UTC instant.
 *   localDate: 'YYYY-MM-DD' (calendar-valid), localTime: 'HH:MM' 24-hour.
 *   - exactly one instant has that wall-clock reading -> { kind: 'ok', ambiguous: false }
 *   - two instants have it (fall-back overlap)        -> { kind: 'ok', ambiguous: true },
 *                                                        utc = the EARLIER instant (first
 *                                                        occurrence = the daylight-time offset)
 *   - none has it (spring-forward gap)                -> { kind: 'nonexistent' }
 *
 * Throws RangeError for a malformed/impossible date or time string or an unknown timeZone (callers
 * validate format first; this is a programming-error guard, not a user-facing path).
 *
 * Assumes at most one UTC-offset transition within +/-24h of the wall time (true for every US zone).
 * Pure and synchronous; uses Intl only.
 */
export function zonedLocalToUtc(localDate, localTime, timeZone) {
    if (!LOCAL_DATE_RE.test(localDate)) {
        throw new RangeError(`Invalid localDate: ${localDate}`);
    }
    if (!LOCAL_TIME_RE.test(localTime)) {
        throw new RangeError(`Invalid localTime: ${localTime}`);
    }
    const [y, m, d] = localDate.split('-').map(Number);
    const [hh, mm] = localTime.split(':').map(Number);
    const wallMs = Date.UTC(y, m - 1, d, hh, mm);
    const roundTrip = new Date(wallMs);
    if (roundTrip.getUTCFullYear() !== y ||
        roundTrip.getUTCMonth() !== m - 1 ||
        roundTrip.getUTCDate() !== d) {
        throw new RangeError(`Impossible calendar date: ${localDate}`);
    }
    const candidateOffsets = new Set([
        offsetMinutesAt(wallMs - DAY_MS, timeZone),
        offsetMinutesAt(wallMs + DAY_MS, timeZone),
    ]);
    const instants = [];
    for (const offset of candidateOffsets) {
        const instant = wallMs - offset * 60_000;
        if (offsetMinutesAt(instant, timeZone) === offset)
            instants.push(instant);
    }
    if (instants.length === 0)
        return { kind: 'nonexistent' };
    return {
        kind: 'ok',
        utc: new Date(Math.min(...instants)),
        ambiguous: instants.length > 1,
    };
}
//# sourceMappingURL=timezone.js.map